// Fonts now load globally from styles/globals.css.
import "./proto.css";
import "./rail-collapse.css";

import {
  Activity,
  BookOpen,
  ChevronLeft,
  ChevronRight,
  Gauge,
  LogOut,
  MapPin,
  Shield,
  Snowflake,
  User,
} from "lucide-react";
import { type ComponentType, createContext, useContext, useMemo } from "react";
import { NavLink, Outlet, useNavigate } from "react-router";

import { JJWordmark } from "@/components/jj-wordmark";
import { WelcomeDialog, WelcomeDialogTrigger } from "@/components/welcome-dialog";
import {
  PresenterVotingDialog,
  PresenterVotingDialogCollapsed,
} from "@/features/voting/presenter-voting-dialog";
import { useLogout, useMe } from "@/lib/api";
import { useDataLabels } from "@/lib/data";
import { logger } from "@/lib/logger";
import { useLocalStorage } from "@/lib/use-local-storage";
import { cn } from "@/lib/utils";

/**
 * SKU Velocity shell — the `.skv` scoping root, the left rail, and the shared top bar.
 *
 * The rail follows the Johnson & Johnson brand shell: a white sidebar with the J&J
 * wordmark up top, a red active block on the current destination, and a warm-gray hover
 * elsewhere. Collapsing flips the whole rail to J&J Red (white marks, inverted active) so
 * the brand reads even in the narrow state — matching the reference support console.
 *
 * The content column carries a shared top bar (product eyebrow + route title + an "Ask the
 * agent" CTA); each screen renders below it inside `<Outlet/>`. Everything still lives inside
 * `.skv` so the ported prototype stylesheet applies 1:1 to the legacy screens.
 */

const RAIL_COLLAPSED_KEY = "skv.rail.collapsed";

/**
 * Lets a routed screen drive the nav rail's collapse (e.g. the Scenarios chat collapses the
 * rail to reclaim width for its chat column, then restores it on close). `useLocalStorage`
 * state isn't shared across hook instances, so the shell owns it and hands the controls down.
 */
interface NavRailControls {
  collapsed: boolean;
  setCollapsed: (next: boolean | ((prev: boolean) => boolean)) => void;
}
const NavRailContext = createContext<NavRailControls | null>(null);

/** Controls for the nav rail's collapse state, or `null` outside the shell. */
export function useNavRail(): NavRailControls | null {
  return useContext(NavRailContext);
}

interface NavItem {
  to: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
}

// Dashboard and "Ask the agent" were pulled from the rail — both surfaces are being
// reworked and will return once adapted. The rail now leads with the unified Scenarios
// screen (merges the former SKU velocity + Capacity scenarios screens) and the two review
// reports; Guide sits at the foot of the group.
const WORKSPACE_NAV: NavItem[] = [
  { to: "/scenarios", label: "Scenarios", icon: Gauge },
  { to: "/reports/storage", label: "Location review", icon: MapPin },
  { to: "/reports/nonmover", label: "Non-movers", icon: Snowflake },
  { to: "/guide", label: "Guide", icon: BookOpen },
];

const ADMIN_NAV: NavItem[] = [
  { to: "/admin/users", label: "Users", icon: Shield },
  { to: "/admin/activity", label: "Activity", icon: Activity },
];

function NavRow({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const Icon = item.icon;
  return (
    <li>
      <NavLink
        to={item.to}
        title={collapsed ? item.label : undefined}
        aria-label={collapsed ? item.label : undefined}
        className={({ isActive }) =>
          cn(
            "group flex items-center rounded-md no-underline transition-colors",
            collapsed ? "h-10 w-full justify-center" : "gap-2.5 px-3 py-2 text-sm",
            collapsed
              ? isActive
                ? "text-primary bg-white"
                : "text-primary-foreground hover:bg-white/10"
              : isActive
                ? "bg-primary text-primary-foreground"
                : "text-foreground hover:bg-warm-gray-02",
          )
        }
      >
        {({ isActive }) => (
          <>
            <Icon
              className={cn(
                "h-4 w-4 shrink-0",
                !collapsed && isActive && "text-primary-foreground",
              )}
            />
            {!collapsed && <span className="truncate">{item.label}</span>}
          </>
        )}
      </NavLink>
    </li>
  );
}

export function SkuVelocityShell() {
  const navigate = useNavigate();
  const logoutMutation = useLogout();
  const { data: me } = useMe();
  // Badge + caption come from the data source's own provenance, so nothing simulated is ever
  // presented as real (R-005) and the copy re-derives itself for the next drop.
  const { data: labels } = useDataLabels();
  const [collapsed, setCollapsed] = useLocalStorage(RAIL_COLLAPSED_KEY, false);
  const navRail = useMemo<NavRailControls>(
    () => ({ collapsed, setCollapsed }),
    [collapsed, setCollapsed],
  );
  const isAdmin = me?.role === "admin";

  async function signOut() {
    logger.debug("[auth] sign out from rail");
    try {
      await logoutMutation.mutateAsync();
    } catch (err) {
      // The cookie may already be gone — land on /login either way.
      logger.warn("[auth] logout failed", err);
    }
    void navigate("/login");
  }

  return (
    <NavRailContext.Provider value={navRail}>
      <div className="skv">
        <div className="app">
          <aside
            className={cn(
              "flex h-full flex-col overflow-hidden border-r transition-[width,background-color,color] duration-200 ease-out",
              collapsed
                ? "bg-primary text-primary-foreground border-primary w-16"
                : "bg-card border-border w-64",
            )}
          >
            {/* Brand header — wordmark routes home; the chevron toggles collapse. */}
            <div
              className={cn(
                "flex items-center border-b",
                collapsed
                  ? "flex-col gap-1 border-white/15 px-2 py-3"
                  : "border-border justify-between gap-2 px-5 py-5",
              )}
            >
              {collapsed ? (
                <>
                  <NavLink
                    to="/scenarios"
                    aria-label="Go to Scenarios"
                    className="font-display text-primary-foreground block py-1 text-base font-bold tracking-tight no-underline hover:opacity-80"
                  >
                    J&amp;J
                  </NavLink>
                  <button
                    type="button"
                    onClick={() => setCollapsed(false)}
                    aria-label="Expand navigation"
                    className="text-primary-foreground flex h-7 w-full items-center justify-center rounded-md transition-colors hover:bg-white/10"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </>
              ) : (
                <>
                  <NavLink
                    to="/scenarios"
                    aria-label="Go to Scenarios"
                    className="rounded-sm no-underline hover:opacity-80"
                  >
                    <JJWordmark size="sm" />
                  </NavLink>
                  <button
                    type="button"
                    onClick={() => setCollapsed(true)}
                    aria-label="Collapse navigation"
                    className="text-muted hover:bg-warm-gray-02 hover:text-foreground inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                </>
              )}
            </div>

            {/* Workspace + Admin nav */}
            <nav className={cn("flex-1 overflow-y-auto py-4", collapsed ? "px-2" : "px-3")}>
              {!collapsed && (
                <p className="text-muted px-2 pb-2 text-[10px] font-medium tracking-[0.18em] uppercase">
                  Workspace
                </p>
              )}
              <ul className="space-y-0.5">
                {WORKSPACE_NAV.map((item) => (
                  <NavRow key={item.to} item={item} collapsed={collapsed} />
                ))}
              </ul>

              {isAdmin && (
                <>
                  {!collapsed ? (
                    <p className="text-muted px-2 pt-6 pb-2 text-[10px] font-medium tracking-[0.18em] uppercase">
                      Admin
                    </p>
                  ) : (
                    <div className="my-3 border-t border-white/15" aria-hidden />
                  )}
                  <ul className="space-y-0.5">
                    {ADMIN_NAV.map((item) => (
                      <NavRow key={item.to} item={item} collapsed={collapsed} />
                    ))}
                  </ul>
                </>
              )}
            </nav>

            {/* Footer — data provenance, profile, sign out. */}
            {collapsed ? (
              <div className="space-y-1 border-t border-white/15 px-2 py-3">
                <NavLink
                  to="/profile"
                  aria-label="Profile"
                  className={({ isActive }) =>
                    cn(
                      "flex h-10 w-full items-center justify-center rounded-md no-underline transition-colors",
                      isActive
                        ? "text-primary bg-white"
                        : "text-primary-foreground hover:bg-white/10",
                    )
                  }
                >
                  <User className="h-4 w-4" />
                </NavLink>
                <PresenterVotingDialogCollapsed />
                <WelcomeDialogTrigger collapsed />
                <button
                  type="button"
                  onClick={() => void signOut()}
                  disabled={logoutMutation.isPending}
                  aria-label="Sign out"
                  className="text-primary-foreground flex h-10 w-full items-center justify-center rounded-md transition-colors hover:bg-white/10 disabled:opacity-50"
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="border-border space-y-2 border-t px-3 py-4">
                <div className="px-2 pb-1">
                  <span className="border-border text-muted inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[9.5px] font-medium tracking-wide uppercase">
                    <span className="bg-primary h-1.5 w-1.5 rounded-full" aria-hidden />
                    {labels?.badge ?? "Checking data"}
                  </span>
                  <p className="text-muted mt-2 text-[10px] leading-relaxed">
                    {labels?.caption ?? "Reading the provenance of this site's feeds…"}
                  </p>
                </div>
                <NavLink
                  to="/profile"
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-2.5 rounded-md px-3 py-2 text-sm no-underline transition-colors",
                      isActive
                        ? "bg-warm-gray-02 text-foreground"
                        : "text-foreground hover:bg-warm-gray-02",
                    )
                  }
                >
                  <User className="h-4 w-4 shrink-0" />
                  <span className="min-w-0 truncate">{me?.email ?? "Profile"}</span>
                </NavLink>
                <PresenterVotingDialog />
                <WelcomeDialogTrigger />
                <button
                  type="button"
                  onClick={() => void signOut()}
                  disabled={logoutMutation.isPending}
                  className="text-foreground hover:bg-warm-gray-02 flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors disabled:opacity-50"
                >
                  <LogOut className="h-4 w-4 shrink-0" />
                  <span>Sign out</span>
                </button>
              </div>
            )}
          </aside>

          {/* The routed screen fills the content column directly. The shared top bar was
            removed to reclaim vertical space — each screen carries its own header/controls,
            and the rail already names the destination. Scrolls vertically: screens fill a
            tall viewport but overflow into a scroll on a short one (or a long doc page like
            the Guide), so nothing — e.g. the field's bottom widget row — is ever unreachable. */}
          <div className="min-h-0 min-w-0 overflow-x-hidden overflow-y-auto">
            <Outlet />
          </div>
        </div>

        {/* Renders nothing on its own — pops the welcome panel on first login
          (when the URL carries `?welcome=1`). */}
        <WelcomeDialog />
      </div>
    </NavRailContext.Provider>
  );
}
